import { useState } from 'react';
import { ChevronDown, ChevronUp, ArrowDown } from 'lucide-react';
import { formatNumberFr, formatPointsFr } from '@/lib/formatters';
import { FAMILY_LABELS } from '../utils';

/**
 * Résumé exécutif pliable, en haut de l'onglet "Lettre & synthèse".
 * Affiche l'Indice Benchory, le delta vs M-1, les 4 sous-scores famille,
 * et un CTA "Lire le rapport complet" qui scrolle vers la lettre.
 */
export default function ExecutiveSummary({ report }) {
  const [open, setOpen] = useState(true);
  const indice = report.indice_benchory;
  const indice_prev = report.indice_benchory_prev;
  const delta = indice != null && indice_prev != null
    ? Math.round((indice - indice_prev) * 10) / 10
    : null;
  const subscores = report.family_subscores || {};

  const scrollToLetter = () => {
    const el = document.querySelector('[data-testid="letter-cabinet"]');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="bg-cabinet-paper border border-cabinet-border shadow-sm p-8" data-testid="executive-summary">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between mb-2 text-left"
        data-testid="executive-summary-toggle"
      >
        <div>
          <div className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.25em] font-medium mb-1">
            Résumé exécutif
          </div>
          <h2 className="font-playfair text-[20px] text-cabinet-ink leading-tight">
            L'essentiel du mois en un coup d'œil
          </h2>
        </div>
        {open
          ? <ChevronUp size={18} className="text-cabinet-ink-secondary shrink-0" />
          : <ChevronDown size={18} className="text-cabinet-ink-secondary shrink-0" />}
      </button>

      {open && (
        <div className="mt-6 space-y-6">
          {/* Indice Benchory */}
          <div className="flex items-baseline gap-6 flex-wrap">
            <div>
              <div className="text-[10px] text-cabinet-ink-secondary uppercase tracking-[0.22em] mb-1">
                Indice Benchory
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-playfair text-[56px] text-cabinet-bordeaux leading-none" data-testid="indice-value">
                  {indice != null ? formatNumberFr(indice, 0) : '—'}
                </span>
                <span className="text-[14px] text-cabinet-ink-secondary">/ 100</span>
              </div>
            </div>
            {delta != null && (
              <div
                className={`text-[11px] uppercase tracking-[0.18em] font-medium ${
                  delta > 0 ? 'text-cabinet-forest'
                  : delta < 0 ? 'text-cabinet-bordeaux'
                  : 'text-cabinet-ink-secondary'
                }`}
                data-testid="indice-delta"
              >
                {formatPointsFr(delta, 1)}
                <span className="text-cabinet-ink-soft normal-case tracking-normal text-[11px] ml-2">
                  vs mois précédent
                </span>
              </div>
            )}
          </div>

          {/* 4 sous-scores famille */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 pt-2">
            {Object.entries(FAMILY_LABELS).map(([key, label]) => (
              <div key={key} className="border-l-2 border-cabinet-gold-soft pl-3" data-testid={`subscore-${key}`}>
                <div className="text-[9px] text-cabinet-ink-secondary uppercase tracking-[0.2em] mb-1">{label}</div>
                <div className="font-playfair text-[24px] text-cabinet-ink leading-none">
                  {subscores[key] != null ? formatNumberFr(subscores[key], 0) : '—'}
                  <span className="text-[11px] text-cabinet-ink-soft ml-1">/100</span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-[12px] font-playfair italic text-cabinet-ink-secondary border-t border-cabinet-border pt-5 leading-[1.7]">
            Pour la lecture détaillée, parcourez la lettre du cabinet et les quatre onglets familles. Le plan d'action concentré se trouve dans le dernier onglet.
          </div>

          <button
            type="button"
            onClick={scrollToLetter}
            data-testid="executive-summary-cta"
            className="inline-flex items-center gap-2 text-[11px] text-cabinet-bordeaux uppercase tracking-[0.2em] hover:text-cabinet-bordeaux-dark transition"
          >
            <ArrowDown size={12} /> Lire le rapport complet
          </button>
        </div>
      )}
    </section>
  );
}
